<template>
  <div
    class="py-4 px-4 h-100"
  >
    <div
      v-if="isPageLoading"
      class="loader mx-auto h-100"
    />
    <template
      v-else
    >
      <section
        class="mb-3"
      >
        <b-row>
          <b-col>
            <base-help-tooltip
              popover-placement="bottom"
            >
              <template v-slot:left>
                <div>
                  <h1 class="page-title">
                    Campaign Overview
                  </h1>
                </div>
              </template>
              <template v-slot:popover-body>
                <p>A brief overview of the key delivery data from this campaign, which also contains a breakdown of the data by individual publishers.</p>
              </template>
            </base-help-tooltip>
          </b-col>
        </b-row>
        <b-row class="mt-6">
          <b-col>
            <ReportStatements
              :type="'CAMPAIGN_OVERVIEW'"
            />
          </b-col>
        </b-row>
      </section>
      <section>
        <CampaignOverviewStats />
      </section>
      <section>
        <div class="my-5">
          <h2 class="page-title">
            Publisher Overview
          </h2>
        </div>
        <b-dropdown
          id="publishers-overview-sort-button"
          class="mb-2"
          :text="'Sort By: ' + getSortByValue(sortBy)"
        >
          <b-dropdown-item
            v-for="(option, index) in sortByOptions"
            :key="index"
            @click="updateSortBy(option)"
          >
            {{ getSortByValue(option) }}
          </b-dropdown-item>
        </b-dropdown>
        <b-row>
          <b-col
            class="col-xl-6 col-xxl-4 col-12"
            v-for="(publisher, index) in sortedPublishersList"
            :key="index"
          >
            <publisher-report-card
              :publisher="publisher"
              :has-scrolls="hasScrollsData"
            />
          </b-col>
        </b-row>
      </section>
    </template>
  </div>
</template>

<script>
import {
  mapActions, mapState,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';

import PublisherReportCard from '@/modules/reporting/components/PublisherReportCard';
import ReportStatements from '@/modules/reporting/components/ReportStatements.vue';
import CampaignOverviewStats from '@/modules/reporting/components/CampaignOverviewStats.vue';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

export default {
  name: 'CampaignOverview',
  components: {
    BaseHelpTooltip,
    PublisherReportCard,
    ReportStatements,
    CampaignOverviewStats,
  },
  data() {
    return {
      isPageLoading: true,
      campaignPublishersList: [],
      publishersStats: [],
      sortBy: 'ctr',
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
    ]),
    sortedPublishersList() {
      return this.getSortedPublishersList(this.sortBy);
    },
    hasScrollsData() {
      return this.publishersStats.some((stats) => stats.scrolls != null);
    },
    sortByOptions() {
      const withScrolls = [
        'name',
        'impressions',
        'clicks',
        'ctr',
        'scrolls',
        'str',
      ];
      const noScrolls = [
        'name',
        'impressions',
        'clicks',
        'ctr',
      ];
      return this.hasScrollsData ? withScrolls : noScrolls;
    },
    fullPublishersDetails() {
      const publishersDetails = [];
      for (let index = 0; index < this.publishersStats.length; index++) {
        const publisherStats = this.publishersStats[index];
        const publisherInfo = this.campaignPublishersList.find((publisher) => publisherStats.publisherId === publisher.id);
        publishersDetails.push({
          publisherInfo,
          publisherStats,
        });
      }
      return publishersDetails;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_PUBLISHERS,
    ]),
    ...mapActions('reporting', [
      reportingTypes.FETCH_CAMPAIGN_PUBLISHER_STATS,
    ]),
    getSortedPublishersList(sortBy) {
      if (sortBy === 'name') {
        return this.fullPublishersDetails.sort((a, b) => a.publisherInfo.name.localeCompare(b.publisherInfo.name));
      }
      return this.fullPublishersDetails.sort((a, b) => b.publisherStats[sortBy] - a.publisherStats[sortBy]);
    },
    createPublisherList(fullPublishersList, publishersStats) {
      const publishersIdsWithStats = publishersStats.map((publisher) => publisher.publisherId);
      const campaignAllPublishersIds = publishersIdsWithStats.concat([...this.order.publisherIds]);
      this.campaignPublishersList = fullPublishersList.flat()
        .filter((publisher) => campaignAllPublishersIds.includes(publisher.id));
    },
    getSortByValue(label) {
      if (label === 'ctr' || label === 'str') return `${label.toUpperCase()}%`;
      return label.toUpperCase();
    },
    updateSortBy(sortBy) {
      this.sortBy = sortBy;
    },
  },
  async created() {
    const { advertiserId, campaignId } = this.$route.params;
    try {
      const payload = {
        advertiserId,
        campaignId,
      };
      Promise.all([this.FETCH_PUBLISHERS({ all: true }), this.FETCH_CAMPAIGN_PUBLISHER_STATS(payload)])
        .then(([fullPublishersList, publishersStats]) => {
          this.createPublisherList(fullPublishersList, publishersStats);
          this.publishersStats = publishersStats;
        });
    } catch (err) {
      this.$router.back();
    } finally {
      this.isPageLoading = false;
    }
  },
};
</script>
